<script setup lang="ts">
import type { PageCTAProps } from '#ui/types';
import lodashOmit from 'lodash-es/omit';
import lodashPick from 'lodash-es/pick';

export interface StoryblokUiPageCtaProps {
    blok: StoryblokUiPageCta;
}

// PROPS
const { blok } = defineProps<StoryblokUiPageCtaProps>();

// COMPOSABLES
const { classes } = useStoryblokComponentClass(() => lodashPick(blok, ['anchorId', 'rounded']));

// DATA
const pageCtaProps = computed<PageCTAProps>(() => ({
    variant: !!blok.variant && (blok.variant === 'gradient' || blok.variant === 'light') ? 'naked' : blok.variant,
    ...lodashOmit(storyblokOmitEmptyValues<StoryblokUiPageCta>(blok), ['_uid', 'component', 'anchorId', 'description', 'links', 'variant']),
}));
const descriptionClasses = computed(() => (blok.variant === 'solid' || blok.variant === 'gradient' ? 'prose-invert' : ''));
const ui = computed(() => {
    switch (blok.variant) {
        case 'gradient':
            return {
                root: 'from-primary-500 to-primary-700 bg-linear-to-r',
                title: 'text-inverted',
                description: 'text-inverted',
            };
        case 'light':
            return {
                root: 'bg-primary-50',
                title: 'text-primary-800',
                description: 'text-default',
            };

        default:
            return {};
    }
});
</script>

<template>
    <UPageCTA v-bind="pageCtaProps" :id="blok.anchorId" v-editable="blok" as="section" :class="classes" :ui="ui">
        <template v-if="!!blok.description" #description>
            <StoryblokRichTextOverride :doc="blok.description" :class="descriptionClasses" class="max-w-none" />
        </template>
        <template v-if="blok.links?.length" #links>
            <StoryblokComponent v-for="l in blok.links" :key="l._uid" :blok="l" />
        </template>
        <template v-if="blok.body?.length" #default>
            <StoryblokComponent v-for="b in blok.body" :key="b._uid" :blok="b" />
        </template>
    </UPageCTA>
</template>
